import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Los planes del Marketplace de Virginia cubren medicamentos para bajar de peso?",
    a: "Por lo general no. El plan de referencia de beneficios esenciales de salud de Virginia excluye específicamente los medicamentos para bajar de peso, así que los planes individuales y de grupos pequeños en Virginia no están obligados a cubrir Zepbound, Wegovy o Saxenda para ese fin, y en general no lo hacen. Un plan sí puede cubrir el mismo medicamento para otra indicación aprobada por la FDA, como la apnea del sueño o la reducción del riesgo cardiovascular.",
  },
  {
    q: "¿Por qué dos personas con la misma tarjeta de seguro tienen cobertura distinta?",
    a: "La mayoría de los empleadores grandes autofinancian su plan de salud y eligen su propio beneficio de farmacia, así que la política publicada por la aseguradora es solo un punto de partida. Además, un administrador de beneficios de farmacia (CVS Caremark, Express Scripts u Optum Rx) decide qué productos específicos están en el formulario. Ambas capas pueden ser distintas entre dos empleados de compañías diferentes, aunque tengan tarjetas de la misma aseguradora.",
  },
  {
    q: "¿Qué requisitos pide Sentara Health Plans?",
    a: "Sentara pide un IMC de 30 o más, o de 27 o más con una condición relacionada con el peso, además de haber participado en un plan de tratamiento para bajar de peso en los últimos seis meses. La aprobación inicial dura 7 meses y la renovación exige al menos un 5 por ciento de pérdida de peso. Un detalle importante: Sentara cubre las plumas (pens) de Zepbound, pero no las presentaciones KwikPen ni de frasco.",
  },
  {
    q: "¿Qué pasa con Anthem?",
    a: "Anthem es la aseguradora comercial más grande de Virginia y su cobertura varía mucho según el tipo de plan. Siempre exige autorización previa cuando cubre estos medicamentos, y su criterio general sigue el estándar de IMC 30, o 27 con una condición asociada, con un requisito de 5 por ciento de pérdida de peso para renovar. Sin embargo, muchos planes de Anthem para empleadores excluyen por completo estos medicamentos del beneficio de farmacia, así que conviene confirmar su plan específico.",
  },
  {
    q: "¿Qué es un administrador de beneficios de farmacia (PBM) y por qué importa?",
    a: "Su aseguradora decide sus beneficios, pero el PBM decide qué medicamentos específicos están en la lista cubierta. CVS Caremark, Express Scripts y Optum Rx manejan la mayoría de las recetas en Virginia, y sus decisiones pueden anular lo que usted esperaría de su aseguradora. Por ejemplo, CVS Caremark retiró Zepbound de su formulario comercial estándar en julio de 2025, aunque Eli Lilly anunció que regresará antes del 1 de octubre de 2026.",
  },
];

export const Route = createFileRoute("/es/seguro/plan-comercial-y-marketplace")({
  head: () => {
    const base = esHead("insuranceCommercial");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("insuranceCommercial"),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/seguro/plan-comercial-y-marketplace#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/seguro/plan-comercial-y-marketplace#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsInsuranceCommercial,
});

function EsInsuranceCommercial() {
  return (
    <EsPageShell
      page="insuranceCommercial"
      title="Planes para empleadores, comerciales y del Marketplace"
      intro="Los planes de empleador, los planes comerciales y los del Marketplace tienen reglas muy distintas entre sí en Virginia. Aquí explicamos, aseguradora por aseguradora, qué exige cada una para cubrir Zepbound, Wegovy y Saxenda, y por qué el administrador de beneficios de farmacia puede cambiar el resultado."
    >
      <EsSection id="resumen" heading="Cómo empezar">
        <p>
          Antes de leer los criterios de cada aseguradora, revise la{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            página general de cobertura de seguro
          </Link>
          , que explica los dos filtros que aplica casi cualquier plan comercial antes de considerar
          los detalles de abajo.
        </p>
      </EsSection>

      <EsSection id="empleador" heading="Planes de empleador y comerciales">
        <p>
          Aquí es donde la cobertura varía más, porque la mayoría de los empleadores grandes
          autofinancian su plan y fijan sus propias reglas. Dos personas con la misma tarjeta y la
          misma aseguradora pueden tener una cobertura completamente distinta.
        </p>
        <dl className="space-y-4">
          <div>
            <dt className="font-medium text-brand-deep">Sentara Health Plans</dt>
            <dd className="mt-1">
              IMC de 30 o más, o de 27 o más con una condición relacionada con el peso, además de
              participación en un plan de tratamiento (asesoría nutricional, ejercicio o dieta con
              restricción calórica) en los últimos seis meses. La aprobación inicial dura 7 meses;
              la renovación exige al menos un 5 por ciento de pérdida de peso. Sentara cubre las
              plumas de Zepbound, pero no las presentaciones KwikPen ni de frasco.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-brand-deep">UnitedHealthcare</dt>
            <dd className="mt-1">
              Aplica solo si el empleador eligió esta cobertura: IMC de 30 o más, o de 27 o más con
              una condición relacionada, junto con cambios en el estilo de vida. Los periodos de
              aprobación inicial varían por medicamento (Saxenda 4 meses, Wegovy 5 meses, Zepbound 6
              meses), y las renovaciones duran 12 meses. UnitedHealthcare no exige, en su criterio
              estándar, haber probado antes otro medicamento más antiguo.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-brand-deep">Cigna</dt>
            <dd className="mt-1">
              Requiere al menos 18 años, al menos tres meses de modificación conductual y
              restricción dietética, e IMC de 30 o más, o de 27 o más con una condición asociada. La
              aprobación inicial es de 8 meses para Zepbound y Wegovy, y de 4 meses para Saxenda; la
              renovación anual exige al menos un 5 por ciento de pérdida de peso (4 por ciento para
              Saxenda), medido contra su peso antes de cualquier GLP-1.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-brand-deep">Anthem Blue Cross and Blue Shield</dt>
            <dd className="mt-1">
              Es la aseguradora comercial más grande de Virginia y su cobertura varía mucho según el
              tipo de plan. Siempre exige autorización previa. En general sigue el estándar de IMC
              30, o 27 con una condición asociada, con un 5 por ciento de pérdida de peso exigido
              para renovar, pero muchos planes de empleador con Anthem excluyen por completo estos
              medicamentos del beneficio de farmacia. Sus beneficios de farmacia los administra
              CarelonRx.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-brand-deep">Aetna</dt>
            <dd className="mt-1">
              Cubre Wegovy y Zepbound para el control crónico del peso, generalmente con IMC de 30 o
              más, o de 27 o más con una condición asociada, además de participación documentada en
              un programa de manejo de peso. Aetna mantiene varias políticas de autorización previa
              entre las que el empleador puede elegir, incluida una con un umbral más alto de IMC
              35. Aetna también administra el plan COVA HealthAware para empleados estatales de
              Virginia.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-brand-deep">CareFirst BlueCross BlueShield y Kaiser Permanente</dt>
            <dd className="mt-1">
              Ambas atienden el norte de Virginia. CareFirst por lo general cubre Wegovy en planes
              comerciales con autorización previa bajo el estándar de IMC 30, o 27 con una condición
              asociada. Kaiser Permanente del Mid-Atlantic exige autorización previa y, por lo
              general, participación en su propio programa clínico de manejo de peso.
            </dd>
          </div>
        </dl>
      </EsSection>

      <EsSection id="pbm" heading="La capa que muchos desconocen: el administrador de beneficios de farmacia">
        <p>
          Su aseguradora decide sus beneficios, pero un administrador de beneficios de farmacia
          (PBM) decide qué medicamentos específicos aparecen en la lista cubierta. Tres PBM —CVS
          Caremark, Express Scripts y Optum Rx— manejan la mayoría de las recetas en Virginia, y sus
          decisiones pueden ir en contra de lo que usted esperaría de su aseguradora.
        </p>
        <p>
          El ejemplo más claro: CVS Caremark retiró Zepbound de su formulario comercial estándar en
          julio de 2025, lo que obligó a muchos pacientes a cambiar a Wegovy. Eli Lilly anunció que
          Zepbound regresará al formulario de CVS Caremark antes del 1 de octubre de 2026. Express
          Scripts y Optum Rx ya cubren Zepbound. Aun así, estar en el formulario de un PBM no
          garantiza que su plan lo cubra, porque el empleador tiene la última palabra.
        </p>
      </EsSection>

      <EsSection id="marketplace" heading="Planes individuales y del Marketplace en Virginia">
        <p>
          El plan de referencia de beneficios esenciales de salud de Virginia excluye
          específicamente la cobertura de medicamentos para bajar de peso. Un informe de la
          Comisión Conjunta de Atención Médica a la Asamblea General lo dice de forma directa: el
          plan de referencia actual excluye la cobertura de estos medicamentos, y por lo tanto no
          hay planes individuales ni de grupo pequeño en Virginia que los cubran.
        </p>
        <p>
          Esto significa que los planes individuales y de grupo pequeño de Virginia no están
          obligados a cubrir Zepbound, Wegovy o Saxenda para bajar de peso, y por lo general no lo
          hacen. Sin embargo, un plan sí puede cubrir semaglutida o tirzepatida para otro uso
          aprobado por la FDA, como la reducción del riesgo cardiovascular o la enfermedad hepática
          MASH. La Oficina de Seguros de Virginia presentó una nueva solicitud de plan de referencia
          ante CMS en mayo de 2026 para el año de cobertura 2028, así que esto podría cambiar.
        </p>
      </EsSection>

      <EsSection id="medicamentos" heading="Notas sobre cada medicamento">
        <p>
          <strong>Zepbound (tirzepatida)</strong>: aprobado para el control crónico del peso y,
          también, para la apnea obstructiva del sueño moderada a grave en adultos con obesidad.
          Solo para mayores de 18 años. Vigile el tipo de dispositivo: Zepbound viene en frascos de
          dosis única, en pluma (Pen) y en KwikPen, y los planes no los tratan de igual manera.
        </p>
        <p>
          <strong>Wegovy (semaglutida)</strong>: el más ampliamente cubierto de los tres en Virginia,
          disponible en inyección, pastilla y en la versión de dosis más alta, Wegovy HD. Tiene tres
          indicaciones aprobadas por la FDA: reducción de eventos cardiovasculares mayores,
          tratamiento de la enfermedad hepática MASH y control crónico del peso.
        </p>
        <p>
          <strong>Saxenda (liraglutida)</strong>: ya existe una versión genérica, que suele ser la
          opción cubierta. Las aprobaciones iniciales suelen ser más cortas (4 meses) y la
          renovación exige un 4 por ciento de pérdida de peso, no un 5 por ciento. Es una inyección
          diaria, no semanal, y en TRICARE es un producto no preferido.
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="mas" heading="Más guías de cobertura">
        <p>
          Consulte también la cobertura de{" "}
          <Link to="/es/seguro/medicaid-y-cardinal-care" className="font-medium text-brand-logo hover:underline">
            Medicaid y Cardinal Care
          </Link>
          , de{" "}
          <Link to="/es/seguro/medicare-tricare-y-fehb" className="font-medium text-brand-logo hover:underline">
            Medicare, TRICARE y FEHB
          </Link>
          , o vuelva al{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general de cobertura
          </Link>
          . Si tiene dudas sobre su caso específico, {" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            contáctenos
          </Link>
          .
        </p>
      </EsSection>
    </EsPageShell>
  );
}
